import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTheme } from "./ThemeProvider";
import { splits as splitsTabelblok } from "../lib/tabelblok";
import { splits as splitsAgendablok, webpaginas } from "../lib/agendablok";
import { splits as splitsTaakblok } from "../lib/taakblok";
import TaakVoorstel from "./TaakVoorstel";
import TaakWijziging from "./TaakWijziging";
import MailAntwoord from "./MailAntwoord";
import remarkTabellen from "../lib/remark-tabellen";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { DENKT, denkTeller, faseTekst, isTaalregel } from "@/lib/beurt";
import { Copy, Check, User, Globe, Users, ThumbsUp, AlertTriangle, Scale, ListTree, Download, Loader2, HardDrive, CloudUpload } from "lucide-react";
import CouncilProgress from "./CouncilProgress";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import ChainState from "./ChainState";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { alsCitaat, blokKop, bronZonderId, datumVeld, deelnemersVeld, extrasVeld, fathomRij, titelVeld, toonWanneer, vertaalVeld, wanneerVeld } from "@/lib/bronwoorden";
import { vasteZin } from "@/lib/vastezinnen";
import AfspraakVoorstel from "./AfspraakVoorstel";
import WorkerVoorstel from "./WorkerVoorstel";
import AppVoorstel from "./AppVoorstel";
import BijlageKaart from "./BijlageKaart";
import CodeRit from "./CodeRit";
import Beeldmerk from "./Beeldmerk";
import AntwoordDocument from "./AntwoordDocument";
import AntwoordOordeel from "./AntwoordOordeel";
import { LOCATIE_TEKST, locatieVan } from "../lib/aanbieder-locatie";
import { HERKOPPEL_ADRES, verlopenBronnen } from "../lib/herkoppel";
import { Link as RouterLink, useInRouterContext } from "react-router-dom";


export default function MessageBubble({ message, isStreaming, onZetInInvoer, onStuur }) {
  const { t } = useTaal();
  const isUser = message.role === "user";

  return (
    <div
      className={cn(
        "flex gap-4 animate-slide-up",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
      data-testid={`message-${message.id}`}
    >
      {/* Avatar */}
      <div
        className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          isUser && "bg-[hsl(var(--secondary))]"
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-foreground" />
        ) : (
          /* J8 (18 okt): het NovaMund-beeldmerk in plaats van het oranje vierkant
           * met de ster; de ring draait zolang het model nadenkt of streamt. */
          <Beeldmerk actief={Boolean(isStreaming || message.bezig)} />
        )}
      </div>

      {/* Content */}
      <div
        className={cn(
          "flex-1 min-w-0",
          isUser ? "flex justify-end" : ""
        )}
      >
        {isUser ? (
          <div className="bg-[hsl(var(--secondary))] px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] inline-block">
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
            {/* Glasronde 17 okt, punt 3: de bijlage blijft zichtbaar in het gesprek. */}
            {(message.attachments || []).map((att, i) => (
              <BijlageKaart key={`${att.name || "bijlage"}-${i}`} att={att} />
            ))}
          </div>
        ) : (
          <div className="prose-chat">
            {message.routing?.p2p && (
              <div className="flex items-center gap-1.5 mb-2 text-[10px] font-medium text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 px-2 py-1 rounded-full w-fit">
                <Globe className="h-3 w-3" />
                {t("Via P2P gerouteerd ({n} deelnemers)", { n: message.routing.peers_available })}
              </div>
            )}
            <ProvenanceBadge message={message} />
            {message.council && <CouncilBadge council={message.council} />}
            {!message.council && message.council_progress?.length > 0 && (
              <CouncilProgress members={message.council_progress} />
            )}
            {message.content ? (
              <>
                {/* GL206-3: een vaste zin van de dienst (de stopzin) in de taal van de app. */}
                <MarkdownContent content={vasteZin(t, message.content)} berichtId={message.id}
                                 onZetInInvoer={onZetInInvoer} onStuur={onStuur}
                                 rit={codeRitVan(message, isStreaming)} />
                <HerkoppelKnoppen tekst={message.content} />
              </>
            ) : isStreaming || message.bezig ? (
              <TypingIndicator fase={message.fase} faseSinds={message.faseSinds} />
            ) : null}
            {message.content && message.denktijd >= 1 && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="denktijd">
                {t("Nagedacht {n} s", { n: message.denktijd })}
              </p>
            )}
            {isStreaming && message.content && (
              <span className="inline-block w-2 h-5 bg-[hsl(var(--accent))] ml-1 animate-pulse-subtle" />
            )}
            {/* GL214-8: alleen de taal veranderd, dan een lichte regel en niet de volle melding. */}
            {message.vervangen && isTaalregel(message.vervangen) && (
              <p className="mt-1 text-[11px] text-muted-foreground/70" data-testid="antwoord-taalgezet">
                {t(message.vervangen)}
              </p>
            )}
            {message.vervangen && !isTaalregel(message.vervangen) && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="antwoord-vervangen">
                {/* De melding komt Nederlands van de dienst (`live.VERVANGEN`); hier
                    in de taal van de schakelaar. Een onbekende tekst blijft staan. */}
                {t(message.vervangen)}
              </p>
            )}
            {message.council && <CouncilDetail council={message.council} />}
            {!isStreaming && message.content && !message.id?.startsWith("temp-") && (
              <ShowYourWork conversationId={message.conversation_id} messageId={message.id} />
            )}
            {/* ZL2a: een duim op dit antwoord, opgeslagen met model, bronnen en tijd. */}
            {!isStreaming && message.content && !message.id?.startsWith("temp-") && (
              <AntwoordOordeel message={message} />
            )}
            {/* J1 (18 okt 2026): bijlagen van het antwoord en de knop Maak document. */}
            {!isStreaming && message.content && !message.id?.startsWith("temp-") && (
              <AntwoordDocument message={message} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const MODE_LABELS = {
  synthesis: "Synthesis",
  consensus: "Consensus (judged)",
  best_of_n: "Best of N",
};

function CouncilBadge({ council }) {
  const { t } = useTaal();
  const verdict = council.verdict || {};
  const dissents = verdict.dissents || [];
  const allAgree = dissents.length === 0 && !council.degraded;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-2">
      <span
        className={cn(
          "flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full w-fit",
          allAgree ? "text-ok bg-ok/10" : "text-corona bg-corona/10"
        )}
      >
        <Users className="h-3 w-3" />
        {MODE_LABELS[council.mode] || council.mode}: {t("{a} van {b} modellen eens", { a: verdict.agreement_count ?? 0, b: verdict.total ?? 0 })}
      </span>
      {council.degraded && (
        <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-corona/10 text-corona">
          {t("Gedeeltelijk oordeel")}
        </span>
      )}
      {council.judge?.is_member && (
        <span
          className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full bg-muted text-muted-foreground"
          title={t("Er was geen model buiten de raad beschikbaar, dus een lid van de raad beoordeelde ook zijn eigen antwoord.")}
        >
          <Scale className="h-3 w-3" />
          {t("Rechter is een lid")}
        </span>
      )}
    </div>
  );
}

function CouncilDetail({ council }) {
  const { t } = useTaal();
  const dissents = council.verdict?.dissents || [];
  return (
    <div className="mt-3 border rounded-xl overflow-hidden">
      <div className="px-3 py-2 bg-muted/50 border-b text-xs font-medium text-muted-foreground">
        {t("Losse antwoorden")}
      </div>
      <div className="divide-y">
        {(council.members || []).map((m, i) => {
          const dissent = dissents.find(d => d.provider === m.provider && (!d.model || d.model === m.model));
          const failed = Boolean(m.error);
          return (
            <div key={i} className="p-3 space-y-1.5">
              <div className="flex items-center gap-2">
                {failed || dissent ? (
                  <AlertTriangle className="h-3.5 w-3.5 text-corona flex-shrink-0" />
                ) : (
                  <ThumbsUp className="h-3.5 w-3.5 text-ok flex-shrink-0" />
                )}
                <span className="text-xs font-medium">{m.provider} / {m.model}</span>
                {typeof m.duration_ms === "number" && (
                  <span className="text-[10px] text-muted-foreground">{(m.duration_ms / 1000).toFixed(1)}s</span>
                )}
              </div>
              {failed ? (
                <p className="text-sm text-corona pl-6">{t("Geen antwoord: {fout}", { fout: m.error })}</p>
              ) : (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap pl-6">{m.response}</p>
              )}
              {dissent && (
                <p className="text-xs text-corona pl-6">{t("Wijkt af: {reden}", { reden: dissent.reason })}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** MK3 (GO Frank 3 nov): het denkniveau zoals de aanbieder het noemt, in de woorden van de kiezer. */
export const NIVEAU_TEKST = { low: "laag", medium: "gemiddeld", high: "hoog", true: "aan", false: "uit" };

/**
 * MK3: het gevraagde denkniveau van dit antwoord, en of het model in dit gesprek is gekozen. Live uit de routering,
 * na herladen uit de generate-stap van het spoor. Een antwoord van voor MK3 heeft geen van beide. PUUR.
 */
export function modelHerkomst(message) {
  const stap = (message?.trail?.steps || []).find((s) => s.type === "generate") || {};
  const routing = message?.routing || {};
  const niveau = routing.denkniveau !== undefined ? routing.denkniveau : stap.denkniveau;
  const gekozen = routing.gesprekskeuze !== undefined ? Boolean(routing.gesprekskeuze) : stap.keuze === "gesprek";
  // MK5 (3 nov): een terugval van lokaal naar lokaal; live uit de routering, na herladen uit de modelstap van het spoor.
  const terugval = routing.terugval ? routing.terugval.naar
    : ((message?.trail?.steps || []).find((s) => s.type === "model" && s.terugval) || {}).terugval || "";
  return { niveau: niveau ? NIVEAU_TEKST[String(niveau)] || String(niveau) : "", gekozen, terugval };
}

/**
 * Says where this answer was actually produced. Both sources are the flight
 * recorder's own numbers: `routing` is what the stream reported as it
 * finished, `trail` is the stored record for a reloaded conversation.
 */
function ProvenanceBadge({ message }) {
  const { t } = useTaal();
  const provider = message.trail?.final_provider || message.routing?.final_provider;
  const model = message.trail?.final_model || message.routing?.final_model;
  if (!provider) return null;
  const plek = t(LOCATIE_TEKST[locatieVan(provider)]);
  const herkomst = modelHerkomst(message);
  const extra = (
    <>
      {herkomst.niveau && <span data-testid="antwoord-denkniveau"> · {t("denken: {niveau}", { niveau: t(herkomst.niveau) })}</span>}
      {herkomst.gekozen && <span data-testid="antwoord-keuze"> · {t("gekozen voor dit gesprek")}</span>}
      {herkomst.terugval && <span data-testid="antwoord-terugval"> · {t("teruggevallen op {model}", { model: herkomst.terugval })}</span>}
    </>
  );

  const leftTheMachine = message.trail
    ? message.trail.left_the_machine
    : provider !== "ollama";
  const escalated = Boolean(message.routing?.escalation) ||
    (message.trail?.steps || []).some(s => s.type === "escalate");

  if (!leftTheMachine) {
    return (
      <div
        className="flex items-center gap-1.5 mb-2 text-[10px] font-medium text-ok bg-ok/10 px-2 py-1 rounded-full w-fit"
        title={`Answered on this machine by ${model || provider}`}
      >
        <HardDrive className="h-3 w-3" />
        {t("Lokaal: {model}", { model: model || provider })}
        <span data-testid="antwoord-locatie"> · {plek}</span>
        {extra}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1.5 mb-2 text-[10px] font-medium text-corona bg-corona/10 px-2 py-1 rounded-full w-fit"
      title={`This prompt was sent to ${provider} (${model || "unknown model"})`}
    >
      <CloudUpload className="h-3 w-3" />
      {escalated ? t("Doorgezet naar") : t("Verstuurd naar")} {provider}{model ? ` / ${model}` : ""}
      <span data-testid="antwoord-locatie"> · {plek}</span>
      {extra}
    </div>
  );
}

// Exported so the agents screen can put the same panel under an agent's
// answer. Nothing about it changes: same fetch, same dialog, same export
// button, and the chat still renders it exactly where it did. An agent run is
// a conversation, so this component already knew how to read one.
export function ShowYourWork({ conversationId, messageId }) {
  const { t } = useTaal();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entry, setEntry] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    if (entry || notFound) return;
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/conversations/${conversationId}/trail`);
      const found = resp.data.entries.find((e) => e.message_id === messageId);
      if (found) setEntry(found);
      else setNotFound(true);
    } catch (err) {
      toast.error(t("Het spoor kon niet worden geladen"));
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const resp = await axios.get(`${API}/conversations/${conversationId}/trail/export?format=markdown`);
      const blob = new Blob([resp.data.content], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = resp.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("Het spoor kon niet worden geëxporteerd"));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
        data-testid="show-your-work-btn"
      >
        <ListTree className="h-3 w-3" />
        {t("Laat je werk zien")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTree className="h-4 w-4" />
              {t("Vluchtrecorder")}
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-8 flex justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : notFound || !entry ? (
            <p className="text-sm text-muted-foreground py-4">{t("Voor dit antwoord is geen spoor opgeslagen.")}</p>
          ) : (
            <div className="space-y-3">
              {/* Wat de keten over DEZE regel zegt, bovenaan en niet als
                  voetnoot. Het is de eerste vraag bij een spoor dat je
                  naslaat: klopt dit nog met wat er is opgeschreven. Het
                  oordeel komt van de server en wordt daar bij elk lezen
                  herberekend; hier wordt niets afgeleid. */}
              <ChainState state={entry.chain} className="text-xs" />
              <ol className="space-y-2">
                {entry.steps.map((step, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="text-muted-foreground font-mono text-xs mt-0.5">{i + 1}.</span>
                    <span className="flex-1">{step.description}</span>
                    {/* R31. DE DUUR VAN DEZE STAP, en alleen wanneer zij is
                        GEMETEN. `typeof` en niet een waarheidstoets: een echte
                        meting van 0 ms zou bij een waarheidstoets wegvallen, en
                        dat is precies het onderscheid dat de backend hier maakt.
                        Een stap zonder veld toont niets, want "0 ms" leest als
                        "het duurde niets" terwijl er niemand heeft gekeken. */}
                    {typeof step.ms === "number" && (
                      <span
                        data-testid="stap-duur"
                        className="text-muted-foreground font-mono text-xs mt-0.5 shrink-0"
                      >
                        {step.ms} ms
                      </span>
                    )}
                  </li>
                ))}
              </ol>
              <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
                <span>{entry.final_provider} / {entry.final_model} · {entry.duration_ms}ms</span>
                <Button variant="ghost" size="sm" onClick={handleExport} className="h-7 gap-1.5 text-xs">
                  <Download className="h-3 w-3" />
                  {t("Exporteren")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Het tabelblok onder `[uit de tabel]`, als echte tabel. R40-1 en R40-2.
 *
 * WAAROM HIER EN NIET IN DE MARKDOWN: zie src/lib/tabelblok.js. Kort: dit blok
 * schrijven wij zelf en het draagt een eigen merkteken, dus de reparatie hoort
 * hier en niet in de opmaak van elk bericht.
 *
 * DE TABEL SCHUIFT ZIJDELINGS IN ZIJN EIGEN VENSTER (`overflow-x-auto`) en de
 * bladzijde niet. Een cel mag 300 tekens dragen, dus een rij is breder dan een
 * telefoon; zonder eigen venster duwt hij het hele gesprek scheef.
 */
function TabelUitDeKennis({ tabel }) {
  const { t } = useTaal();
  return (
    <div className="mt-3 border-t border-[hsl(var(--border))] pt-3"
         data-testid="tabelblok">
      <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
        {t("uit de tabel")}
        {tabel.bron && (
          <span className="ml-1 font-medium" data-testid="tabelblok-bron">
            ({tabel.bron})
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="text-sm border-collapse" data-testid="tabelblok-tabel">
          <thead>
            <tr>
              {tabel.kop.map((cel, i) => (
                <th key={i}
                    className="border border-[hsl(var(--border))] px-2 py-1 text-left align-top font-medium whitespace-normal break-words max-w-[22rem]">
                  {cel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tabel.rijen.map((rij, r) => (
              <tr key={r}>
                {rij.map((cel, c) => (
                  <td key={c}
                      className="border border-[hsl(var(--border))] px-2 py-1 align-top whitespace-normal break-words max-w-[22rem]">
                    {cel}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * De dag onder `[uit de bronnen]`, als bewijs. R57 punt B.
 *
 * **FRANKS GLASTEST VAN 0.9.108: "wat ik NIET zie is de bron met een id per
 * agendaregel."** De herkomst stond wel in het materiaal en dat is een verzoek
 * aan het model; gpt-oss negeerde het drie keer op drie. Dit blok komt van de
 * backend en hangt van geen enkel model af.
 *
 * DE BRON MET DE ID STAAT ONDER ELKE REGEL en niet achteraan in een voetnoot.
 * Dat is de hele eis: een afspraak die Frank niet kan terugvinden, is het soort
 * antwoord waar R37 en R40 tegen bestaan.
 *
 * ZELFDE OPMAAK ALS `TabelUitDeKennis`, met opzet: twee bewijsblokken op een
 * scherm horen er niet verschillend uit te zien omdat ze langs een andere weg
 * zijn gerenderd.
 */
export function AgendaUitDeKennis({ agenda }) {
  const { t } = useTaal();
  // WELKE MAILREGEL OPEN STAAT, en er staat er hooguit EEN open. Twee
  // antwoordvakken naast elkaar op hetzelfde scherm is precies de opzet waarin
  // iemand in het verkeerde vak typt en het in de andere draad verstuurt.
  const [antwoordOp, setAntwoordOp] = useState(null);
  return (
    // C3: een eigen vak met achtergrond en een label, zodat het bronblok niet
    // leest als het vervolg van het antwoord ("vijf zinnen gaf zes").
    <div className="mt-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-3 py-2"
         role="note" aria-label={t("Bronnen onder dit antwoord")}
         data-testid="agendablok">
      {/* **DE BRONNEN KOMEN UIT DE HERKOMST EN STAAN NIET MEER IN DEZE TEKST.
          R57, gebrek 3 van 27 augustus 2026.** Hier stond letterlijk "uit de
          agenda", en dat woord stond dus ook boven een blok met vier mails.
          De backend zet de bevraagde bronnen vooraan in de kop; dit scherm
          toont ze en verzint er geen. Zonder bronnen staat er "je bronnen",
          want een kale "uit" is geen zin. */}
      <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] mb-2">
        <ListTree className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {/* GL212-3: geen los "uit" voor een kop die al een hele zin is. */}
        {blokKop(t, agenda.herkomst).voor && <span>{blokKop(t, agenda.herkomst).voor}</span>}
        <span className="font-medium" data-testid="agendablok-herkomst">
          {blokKop(t, agenda.herkomst).kop}
        </span>
      </div>
      <ul className="space-y-1.5">
        {agenda.rijen.map((rij, i) => fathomRij(rij) ? (
          // GL214-2: een gesprek uit Fathom, leesbaar: titel, datum, deelnemers en een link.
          <li key={i} className="text-sm" data-testid="agendablok-rij">
            <div className="font-medium" data-testid="fathom-titel">{fathomRij(rij).titel}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="fathom-wanneer">
              {[datumVeld(t, fathomRij(rij).datum), fathomRij(rij).tijd].filter(Boolean).join(", ")}
            </div>
            {fathomRij(rij).deelnemers && (
              <div className="text-xs" data-testid="fathom-deelnemers">
                {t("Aanwezig")}: {deelnemersVeld(t, fathomRij(rij).deelnemers)}
              </div>
            )}
            {fathomRij(rij).link && (
              <a href={fathomRij(rij).link} target="_blank" rel="noopener noreferrer"
                 className="text-xs underline text-[hsl(var(--accent))]" data-testid="fathom-link">
                {t("Terugkijken in Fathom")}
              </a>
            )}
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] break-all"
                 title={bronZonderId(rij.bron).ids || undefined}
                 data-testid="agendablok-bron">
              {vertaalVeld(t, bronZonderId(rij.bron).naam)}
            </div>
          </li>
        ) : (
          <li key={i} className="text-sm" data-testid="agendablok-rij">
            {toonWanneer(rij.wanneer) && (
              <span className="font-mono text-xs mr-2 whitespace-nowrap" data-testid="agendablok-wanneer">{wanneerVeld(t, rij.wanneer)}</span>
            )}
            {/* GL211-5: webpagina's als lijst met per bron het domein, aanklikbaar. */}
            {webpaginas(rij) ? (
              <span>
                {/* GL220-10: de telling in de kop, niet los onder de lijst. */}
                <span data-testid="webbronnen-kop">
                  {vertaalVeld(t, "Webpagina")}
                  {rij.extras && <span className="ml-2 text-[hsl(var(--muted-foreground))]">{extrasVeld(t, rij.extras)}</span>}
                </span>
                <ul className="mt-1 ml-1 space-y-0.5" data-testid="webbronnen">
                  {webpaginas(rij).map((w) => (
                    <li key={w.url}>
                      <a href={w.url} target="_blank" rel="noopener noreferrer" title={w.url}
                         className="underline text-[hsl(var(--accent))] break-all" data-testid="webbron">{w.naam}</a>
                    </li>
                  ))}
                </ul>
              </span>
            ) : (
              <span>{alsCitaat(titelVeld(t, rij))}</span>
            )}
            {rij.extras && !webpaginas(rij) && (
              <span className="ml-2 text-[hsl(var(--muted-foreground))]">{extrasVeld(t, rij.extras)}</span>
            )}
            {/* GL208-2: geen agenda-id in beeld; hij staat in de tooltip. `break-all` zorgt dat
                een id die wel getoond wordt (Drive, TASQ, mail) nooit buiten beeld loopt.
                GL220-10: bij webpagina's staat het label al in de kop; dan geen los "Webpagina" eronder. */}
            {!webpaginas(rij) && (
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] break-all"
                   title={bronZonderId(rij.bron).ids || undefined}
                   data-testid="agendablok-bron">
                {vertaalVeld(t, bronZonderId(rij.bron).naam)}
              </div>
            )}
            {/* DE WEG NAAR HET ANTWOORDSCHERM. R57 fase 3.
                Zonder deze knop bestaat `MailAntwoord` wel in de code en is er
                geen scherm dat een mens kan bereiken - precies het gebrek dat
                dit project keer op keer tegenkomt. Alleen MAILregels krijgen
                hem, en `mailUit` beslist dat op de BRON en niet op de titel. */}
            {rij.mailId && (
              antwoordOp === rij.mailId ? (
                <div className="mt-2">
                  <MailAntwoord berichtId={rij.mailId} afzender={rij.extras}
                                onderwerp={rij.titel} />
                </div>
              ) : (
                <button type="button"
                        className="mt-1 text-[11px] underline text-[hsl(var(--accent))]"
                        onClick={() => setAntwoordOp(rij.mailId)}
                        data-testid="agendablok-antwoord-btn">
                  {t("Beantwoorden")}
                </button>
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Gmail-herstel (24 okt 2026): noemt het antwoord een verlopen Google-koppeling, dan staat er
 * een knop onder die Instellingen op Verbindingen opent. Daar staat Opnieuw koppelen.
 */
export function HerkoppelKnoppen({ tekst }) {
  const { t } = useTaal();
  const inRouter = useInRouterContext();
  const namen = verlopenBronnen(tekst);
  if (!namen.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="herkoppel-knoppen">
      {namen.map((naam) => {
        const label = t("{bron} opnieuw koppelen", { bron: naam });
        return inRouter ? (
          <Button key={naam} asChild size="sm" variant="outline" data-testid="herkoppel-knop">
            <RouterLink to={{ search: HERKOPPEL_ADRES }}>{label}</RouterLink>
          </Button>
        ) : (
          <Button key={naam} asChild size="sm" variant="outline" data-testid="herkoppel-knop">
            <a href={`/${HERKOPPEL_ADRES}`}>{label}</a>
          </Button>
        );
      })}
    </div>
  );
}

/** NS-C1: een antwoord van de assistent dat is opgeslagen, mag zijn codeblokken laten draaien. PUUR. */
export function codeRitVan(message, isStreaming) {
  if (isStreaming || message?.role !== "assistant" || !message?.id || message.id.startsWith("temp-")) return null;
  return { gesprekId: message.conversation_id, berichtId: message.id, bron: message.content };
}

function MarkdownContent({ content, berichtId, onZetInInvoer, onStuur, rit = null }) {
  const { theme } = useTheme();
  // POST 10c STAP 1: de tekst die de server teruggeeft nadat de werkruimte is
  // gekozen. De vraagkop hoort bij de vraag, en de vraag is beantwoord; zonder
  // dit bleef hij boven het voorstel staan tot iemand de pagina herlaadde.
  const [naKeuze, setNaKeuze] = useState(null);
  // R40-1 en R57-B. Allebei de bewijsblokken gaan langs de markdown heen; zie
  // TabelUitDeKennis en AgendaUitDeKennis.
  //
  // DE AGENDA WORDT EERST AFGESPLITST, want de backend plakt hem als LAATSTE
  // aan. Andersom zou het tabelblok de agenda in zijn eigen staart meenemen.
  const { tekst: zonderAgenda, agenda } = splitsAgendablok(content);
  // HET TAAKBLOK STAAT VOOR HET BRONBLOK in de tekst, dus het wordt DAARNA
  // afgesplitst: `splitsAgendablok` knipt op `[uit de bronnen]` en laat het
  // taakblok in zijn kop staan. Andersom zou het taakblok binnen het bronblok
  // vallen en stil verdwijnen; datzelfde staat in `taakblok.py` opgeschreven.
  const { tekst: zonderTaak, taak } = splitsTaakblok(zonderAgenda);
  const { tekst, tabel } = splitsTabelblok(zonderTaak);

  if (tabel || agenda || taak) {
    return (
      <>
        <MarkdownContent content={naKeuze ?? tekst} onZetInInvoer={onZetInInvoer} onStuur={onStuur} rit={rit} />
        {tabel && <TabelUitDeKennis tabel={tabel} />}
        {agenda && <AgendaUitDeKennis agenda={agenda} />}
        {taak && (
          <div className="mt-2" data-testid="taakblok">
            {taak.soort === "afspraak" ? (
              <AfspraakVoorstel voorstelId={taak.id} />
            ) : taak.soort === "worker" ? (
              <WorkerVoorstel voorstelId={taak.id} onAangemaakt={setNaKeuze} />
            ) : taak.soort === "app" ? (
              <AppVoorstel voorstelId={taak.id} />
            ) : taak.soort === "wijziging" ? (
              <TaakWijziging wijzigingId={taak.id} keuze={taak.keuze} zin={taak.zin}
                             berichtId={berichtId} onGekozen={setNaKeuze}
                             onWijzig={onZetInInvoer} />
            ) : (
              <TaakVoorstel voorstelId={taak.id} keuze={taak.keuze} zin={taak.zin}
                            berichtId={berichtId} onGekozen={setNaKeuze}
                            onWijzig={onZetInInvoer} onOpnieuw={onStuur} />
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkTabellen]}
      components={{
        /* De tabelopmaak is dezelfde als die van het R40-blok hierboven, met
         * opzet: twee tabellen op een scherm horen er niet verschillend uit te
         * zien omdat ze langs een andere weg zijn gerenderd.
         *
         * DE TABEL SCHUIFT ZIJDELINGS IN ZIJN EIGEN VENSTER en de bladzijde
         * niet. Zonder dat eigen venster duwt een brede tabel het hele gesprek
         * scheef, en dat is precies de klasse opmaakfout die op 24 augustus
         * 2026 een dag heeft gekost. Er staat een meting op, zie
         * `frontend/meting/opmaak.mjs`. */
        /* GL220-5: een link naar buiten (de bestellink van een koppeling) opent in de echte browser. De
         * desktop opent alleen een nieuw venster daar (`setWindowOpenHandler` in desktop/main.js); een
         * gewone link zou het venster van NovaMund zelf wegsturen. */
        a: ({ node, href, ...props }) => (/^https?:\/\//i.test(href || "")
          ? <a href={href} target="_blank" rel="noopener noreferrer" {...props} />
          : <a href={href} {...props} />),
        table: ({ node, ...props }) => (
          <div className="overflow-x-auto my-3" data-testid="markdown-tabel">
            <table className="text-sm border-collapse" {...props} />
          </div>
        ),
        th: ({ node, ...props }) => (
          <th className="border border-[hsl(var(--border))] px-2 py-1 text-left align-top font-medium whitespace-normal break-words max-w-[22rem]"
              {...props} />
        ),
        td: ({ node, ...props }) => (
          <td className="border border-[hsl(var(--border))] px-2 py-1 align-top whitespace-normal break-words max-w-[22rem]"
              {...props} />
        ),
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || "");
          const language = match ? match[1] : "";
          const codeString = String(children).replace(/\n$/, "");

          if (!inline && (match || codeString.includes("\n"))) {
            return (
              <CodeBlock
                code={codeString}
                language={language}
                theme={theme}
                rit={rit}
              />
            );
          }

          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function CodeBlock({ code, language, theme, rit = null }) {
  const { t } = useTaal();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t("Gekopieerd"));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t("Kopiëren mislukt"));
    }
  };

  return (
    <div className="relative group rounded-lg overflow-hidden my-4 border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[hsl(var(--muted))] border-b">
        <span className="text-xs font-mono text-muted-foreground uppercase">
          {language || "code"}
        </span>
        <span className="flex items-center gap-1">
        <CodeRit rit={rit} code={code} />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 px-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
          data-testid="copy-code-btn"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-ok" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
        </span>
      </div>
      
      {/* Code */}
      <SyntaxHighlighter
        style={theme === "dark" ? oneDark : oneLight}
        language={language || "text"}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: "1rem",
          background: theme === "dark" ? "hsl(0 0% 12%)" : "hsl(0 0% 98%)",
          fontSize: "0.875rem",
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

/** Fix 1: de bezig-aanduiding, met de fase die de dienst meldt eronder. */
function TypingIndicator({ fase, faseSinds }) {
  // Punt 4a: tijdens het nadenken telt er een seconde-teller mee.
  const denkt = fase === DENKT && !!faseSinds;
  const [nu, setNu] = useState(() => Date.now());
  useEffect(() => {
    if (!denkt) return undefined;
    const klok = setInterval(() => setNu(Date.now()), 1000);
    return () => clearInterval(klok);
  }, [denkt]);
  return (
    <div className="flex flex-col gap-1" role="status" aria-live="polite">
      <div className="typing-indicator" data-testid="typing-indicator">
        <span />
        <span />
        <span />
      </div>
      {fase && (
        <span className="text-xs text-muted-foreground" data-testid="typing-fase">
          {faseTekst(fase)}
          {denkt && ` ${denkTeller(faseSinds, nu)}`}
        </span>
      )}
    </div>
  );
}
